import React from 'react';
import { ChevronDown } from 'lucide-react';
import { TONE_BORDER, TONE_SOFT } from './tone';

/**
 * A tinted note.
 *
 * Two kinds of note were being written the same way, and only one of them
 * earns permanent space on the page.
 *
 * An ACTIONABLE note — "4 documents expire in the next 60 days", "3 things to
 * look at before locking" — is a queue of work. It stays open, because the
 * whole point is that somebody sees it without asking.
 *
 * An EXPLANATORY note — how payroll components resolve, why a balance
 * component matters — is reference. It is read once, understood, and then
 * re-read by nobody, but it keeps occupying four lines above the thing people
 * actually came to use. Those pass `collapsible` and sit as a single quiet
 * line until opened.
 *
 * Both are tighter than what they replace: `px-3 py-2 text-xs` against the
 * old `px-4 py-3 text-sm`, which is roughly a third less vertical space per
 * note on screens that often stack two or three.
 *
 *   <Callout tone="warning" icon={<AlertTriangle size={14} />}>
 *     3 policies are waiting to be accepted.
 *   </Callout>
 *
 *   <Callout tone="info" collapsible title="How components resolve">
 *     Fixed amounts, then % of gross…
 *   </Callout>
 */
const Callout = ({
  tone = 'info',
  icon,
  title,
  children,
  collapsible = false,
  defaultOpen = false,
  className = ''
}) => {
  const shell = `rounded-control border px-3 py-2 text-xs ${
    TONE_BORDER[tone] ?? TONE_BORDER.info
  } ${className}`;

  if (collapsible) {
    return (
      <details className={`group ${shell}`} open={defaultOpen}>
        <summary
          className={`flex cursor-pointer list-none items-center gap-1.5 font-medium marker:hidden ${
            (TONE_SOFT[tone] ?? TONE_SOFT.info).split(' ').pop()
          }`}>
          {icon}
          <span>{title ?? 'Why this matters'}</span>
          <ChevronDown
            className="ml-auto h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-180"
            aria-hidden="true"
          />
        </summary>
        <div className="mt-1.5 leading-relaxed text-gray-600">{children}</div>
      </details>
    );
  }

  return (
    <div className={`flex items-start gap-2 ${shell}`}>
      {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
      <div className={(TONE_SOFT[tone] ?? TONE_SOFT.info).split(' ').pop()}>
        {title && <div className="font-semibold">{title}</div>}
        {children}
      </div>
    </div>
  );
};

export default Callout;
